import { useI18n } from "@baton/i18n";
import type { AnchorHTMLAttributes, ReactNode, Ref } from "react";
import { Link as RouterLink } from "react-router";
import { VisuallyHidden } from "../../layout/VisuallyHidden/VisuallyHidden.tsx";
import { linkClassName, type LinkVariant } from "./linkClassName.ts";

/**
 * FR-015-150, § Primitives, Link: the inline and standalone variants; an instance-relative
 * `to` routes through the router; an external `href` gets `rel="noopener noreferrer"`,
 * `target="_blank"` and a visually hidden "opens in a new tab" suffix in its name. The space
 * that separates the suffix from the link's text is inside the hidden span, so the underline of a
 * link in a sentence ends at its last character and no drawn space is underlined (2026-10-07).
 * Custom in `packages/ui`, its classes in `linkClassName.ts` (2026-09-15, slice (e)).
 */
export interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  readonly to?: string;
  readonly href?: string;
  readonly external?: boolean;
  readonly variant?: LinkVariant;
  readonly children: ReactNode;
  readonly ref?: Ref<HTMLAnchorElement>;
}

/** An `href` is external when it is absolute with a scheme or scheme-relative. */
export function isExternalHref(href: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith("//");
}

export function Link({ to, href, external, variant = "inline", className, children, ref, ...rest }: LinkProps) {
  const { t } = useI18n();
  const classes = linkClassName(variant, className);
  if (to !== undefined && external !== true) {
    return (
      <RouterLink ref={ref} to={to} data-slot="link" className={classes} data-variant={variant} {...rest}>
        {children}
      </RouterLink>
    );
  }
  const target = href ?? to ?? "";
  const isExternal = external ?? isExternalHref(target);
  if (isExternal) {
    return (
      <a ref={ref} href={target} data-slot="link" className={classes} data-variant={variant} rel="noopener noreferrer" target="_blank" {...rest}>
        {children}
        <VisuallyHidden>{` ${t("ui.link.opensInNewTab")}`}</VisuallyHidden>
      </a>
    );
  }
  return (
    <RouterLink ref={ref} to={target} data-slot="link" className={classes} data-variant={variant} {...rest}>
      {children}
    </RouterLink>
  );
}
